import dayjs from 'dayjs';
import { runInAction } from 'mobx';
import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm, UseFormReturn, useFieldArray } from 'react-hook-form';

import { useTranslation } from '@gem/modules/i18n';
import dateService from '@gem/services/date-service';

import Tasks from 'APP/Tasks';
import { IParticipant, ConfirmationType, ReminderType } from 'APP/model/calendar/calendar.types';
import { getCustomReminderTitle } from 'APP/utils/calendar/getCustomReminderTitle';
import { getHumanizeRRule } from 'APP/utils/calendar/getHumanizeRRule';
import { mapReminderToMinutes } from 'APP/utils/calendar/mapReminderToMinutes';
import Entities from 'STORE';
import { User } from 'STORE/Users/User/User';

import { IEventCalendar, IUpdateEvent } from '../../Content.types';
import {
  ReminderMap,
  ReminderTimeEnum,
  RepeatEnum,
  RepeatMap,
  REMINDERS_MAX_LENGTH,
} from './Form.constants';
import { IFormData, IFormUser } from './Form.types';
import { ICalendarEventSubmitData } from './Form.types';
import { CustomReminderPopup } from './Popup/CustomReminderPopup/CustomReminderPopup';
import { CustomRepeatPopup } from './Popup/CustomRepeatPopup/CustomRepeatPopup';
import { customResolver } from './validation/customResolver';

interface IFromPresenter {
  initialValues: IFormData;
  diff: number;
  diffMinutes: number;
  link: string;
  initialize(): Promise<void>;
  onSubmit(data: IFormData): Promise<void>;
  selectedParticipants: Array<IFormUser>;
  reminderItems: { key: string; title: string }[];
  repeatItems: { key: string; title: string }[];
  setDiff: (value: number) => void;
  setDiffMinutes(value: number): void;
  onChangeTitle(value: string): void;
  onChangeCheckbox(value: boolean): void;
  onChangeStartAllDay(value: Date): void;
  onChangeEndAllDay(value: Date): void;
  onChangeStart(value: Date): void;
  onChangeStartTime(value: string): void;
  onChangeEnd(value: Date): void;
  onChangeEndTime(value: string): void;
  removeParticipant(email: string): void;
  setParticipant(user: User): void;
  setParticipants(users: User[]): void;
  setCustomRepeat(data: { key: string; title: string } | null): void;
  customRepeat: { key: string; title: string } | null;
  getMinimizedTitleInfo: () => string;
  getMinimizedTimeInfo: () => string;
  onChangeReminderItem(value: string, index: number): void;
  onChangeRepeat(currentRepeatValue: string): void;
  onChangeDescription(value: string): void;
}

interface IFormPresenterReturnType {
  presenter: IFromPresenter;
  form: UseFormReturn<IFormData>;
  participantsValue: string[];
  removeChip(id: string): void;
  controlledReminderFields: Array<{ id: string; value: string }>;
}

interface IFormPresenterProps {
  event: IEventCalendar;
  participantsInfo?: User[];
  updateEvent: (params: IUpdateEvent) => void;
  jumpTo: (date: Date) => void;
  handleSubmit: (params: ICalendarEventSubmitData) => Promise<void>;
  isCreate?: boolean;
}

export function useFormPresenter(props: IFormPresenterProps): IFormPresenterReturnType {
  const { t } = useTranslation();

  const presenter = useLocalObservable<IFromPresenter>(() => ({
    initialValues: {
      title: props.event.isSelected ? props.event.title : '',
      description: props.event.isSelected ? props.event.description : '',
      reminders: props.event.reminders.length
        ? props.event.reminders.map((reminder) => {
            return {
              value: mapReminderToMinutes(reminder.time, reminder.unit),
            };
          })
        : [{ value: ReminderTimeEnum.None }],
      repeat: props.event.recurrenceType ?? RepeatEnum.NONE,
      participants: props.event.participants.length
        ? [...props.event.participants.map((participant) => participant.email)]
        : [props.event.initiatorEmail],
      allDay: false,
      start: new Date(),
      end: new Date(),
      startTime: dayjs().format('HH:mm'),
      endTime: dayjs().add(1, 'hour').format('HH:mm'),
    },
    diff: 0,
    diffMinutes: 0,
    link: props.event.isSelected ? props.event.callLink : '',
    selectedParticipants: [],
    customRepeat: null,

    async initialize(): Promise<void> {
      const user = Entities.usersStore.getUserById(Entities.usersStore.myId!);

      if (user) {
        presenter.selectedParticipants = [
          {
            id: user.id,
            displayName: user.displayName,
            avatarUrl: user.avatarUrl || '',
            avatarColorIndex: user.avatarColorIndex,
            email: props.event.initiatorEmail,
          },
        ];
      }

      if (props.isCreate) {
        const response = await Tasks.calls.createRoom({
          roomName: t('new_meeting_modal_name_value', {
            0:
              dateService.format(new Date(), {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              }) || '',
          }),
          allowGuests: true,
          waitingRoomEnabled: false,
          webinar: false,
          initialParticipants: [],
        });

        const { link } = response;

        runInAction(() => {
          presenter.link = link || '';
        });
      }
    },

    async onSubmit(params: IFormData): Promise<void> {
      const dayName = new Intl.DateTimeFormat('en-US', { weekday: 'short' })
        .format(params.start)
        .toLocaleUpperCase();

      let repeatFinal = `${params.repeat}`;

      if (params.repeat === RepeatEnum.WorkingDays) {
        repeatFinal = `WEEKLY;BYDAY=MO,TU,WE,TH,FR`;
      }

      if (params.repeat === RepeatEnum.WEEKLY) {
        repeatFinal = `WEEKLY;BYDAY=${dayName.substring(0, 2)}`;
      }

      if (params.repeat === RepeatEnum.MONTHLY) {
        repeatFinal = `MONTHLY`;
      }

      await props.handleSubmit({
        ...params,
        repeat: repeatFinal,
        callLink: presenter.link,
      });
    },

    removeParticipant(email: string): void {
      presenter.selectedParticipants = presenter.selectedParticipants.filter(
        (iten) => iten.email !== email
      );
    },

    setParticipant(user: User): void {
      presenter.selectedParticipants = [
        ...presenter.selectedParticipants,
        {
          id: user.id,
          displayName: user.displayName,
          avatarUrl: user.avatarUrl || '',
          avatarColorIndex: user.avatarColorIndex,
          email: user.email,
        },
      ];
    },

    setParticipants(users: User[]): void {
      const ids = users.map((user) => user.id);

      const uniqUsers = presenter.selectedParticipants.filter((user) => !ids.includes(user.id));

      presenter.selectedParticipants = [
        ...uniqUsers,
        ...users.map((user) => ({
          id: user.id,
          displayName: user.displayName,
          avatarUrl: user.avatarUrl || '',
          avatarColorIndex: user.avatarColorIndex,
          email: user.email,
        })),
      ];
    },

    get reminderItems(): { key: string; title: string }[] {
      const commonReminders = Object.entries(ReminderMap).map(([key, value]) => ({
        key,
        title: t(value),
      }));

      const currentReminders = form.getValues().reminders;
      const uncommonReminders = [];

      for (const i in currentReminders) {
        if (!Object.hasOwn(ReminderMap, currentReminders[i].value)) {
          uncommonReminders.push({
            key: currentReminders[i].value,
            title: getCustomReminderTitle(Number(currentReminders[i].value)),
          });
        }
      }

      return [...commonReminders, ...uncommonReminders];
    },

    get repeatItems(): { key: string; title: string }[] {
      let items = Object.entries(RepeatMap).map(([key, value]) => ({
        key,
        title: t(value),
      }));

      if (presenter.customRepeat) {
        items = [...items, presenter.customRepeat];
      }

      return items;
    },

    setCustomRepeat(data: { key: string; title: string } | null): void {
      presenter.customRepeat = data;
    },

    setDiff(value: number): void {
      presenter.diff = value;
    },

    setDiffMinutes(value: number): void {
      presenter.diffMinutes = value;
    },

    onChangeTitle(value: string): void {
      props.updateEvent({ title: value || `(${t('calendar_notitle')})` });
    },

    onChangeCheckbox(value: boolean): void {
      let params: {
        allDay: boolean;
        start?: Date;
        end?: Date;
      } = { allDay: value };
      const { start, end, startTime, endTime } = form.getValues();

      if (value) {
        const startDate = dayjs(start).hour(0).minute(0).second(0).millisecond(0).toDate();
        const endDate = dayjs(end)
          .add(1, 'day')
          .hour(0)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate();

        params = {
          ...params,
          start: startDate,
          end: endDate,
        };
      } else {
        const [HH, mm] = startTime.split(':');
        const [HHEnd, mmEnd] = endTime.split(':');
        const startDate = dayjs(start).hour(+HH).minute(+mm).second(0).millisecond(0).toDate();

        const endDate = dayjs(end).hour(+HHEnd).minute(+mmEnd).second(0).millisecond(0).toDate();

        params = {
          ...params,
          start: startDate,
          end: endDate,
        };
      }

      props.updateEvent(params);
    },

    onChangeStartAllDay(value: Date): void {
      const { end } = form.getValues();
      const startDate = dayjs(value).hour(0).minute(0).second(0).millisecond(0);
      let endDate = null;

      if (startDate.isSame(end, 'day') || startDate.isAfter(end, 'day')) {
        endDate = startDate.add(presenter.diff || 1, 'day');
        props.updateEvent({
          start: startDate.toDate(),
          end: endDate.toDate(),
        });
      } else {
        props.updateEvent({
          start: startDate.toDate(),
        });
      }

      props.jumpTo(value);
    },

    onChangeEndAllDay(value: Date): void {
      const endDate = dayjs(value)
        .add(1, 'day')
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate();

      props.updateEvent({
        end: endDate,
      });
    },

    onChangeStart(value: Date): void {
      const { end, startTime, endTime } = form.getValues();

      const [HH, mm] = startTime.split(':');
      const [HHEnd, mmEnd] = endTime.split(':');
      const startDate = dayjs(value).hour(+HH).minute(+mm);
      const isStartAfterEnd = startDate.isAfter(end);
      let endDate = null;

      if (isStartAfterEnd) {
        endDate = startDate.hour(+HHEnd).minute(+mmEnd).toDate();
        props.updateEvent({
          start: startDate.toDate(),
          end: endDate,
        });
      } else {
        props.updateEvent({
          start: startDate.toDate(),
        });
      }

      props.jumpTo(value);
    },

    onChangeStartTime(value: string): void {
      const { start } = form.getValues();
      const [HH, mm] = value.split(':');
      const startDate = dayjs(start).hour(+HH).minute(+mm);

      props.updateEvent({
        start: startDate.toDate(),
        end: startDate.add(presenter.diffMinutes, 'minutes').toDate(),
      });

      form.setValue('startTime', value, {
        shouldValidate: true,
      });
    },

    onChangeEnd(value: Date): void {
      const { endTime } = form.getValues();

      const [HHEnd, mmEnd] = endTime.split(':');
      const endDate = dayjs(value).hour(+HHEnd).minute(+mmEnd);

      props.updateEvent({
        end: endDate.toDate(),
      });
    },

    onChangeEndTime(value: string): void {
      const { end } = form.getValues();
      const [HH, mm] = value.split(':');
      const endDate = dayjs(end).hour(+HH).minute(+mm);

      props.updateEvent({
        end: endDate.toDate(),
      });

      form.setValue('endTime', value, {
        shouldValidate: true,
      });
    },

    onChangeRepeat(currentRepeatValue: string): void {
      if (currentRepeatValue === RepeatEnum.CUSTOM) {
        const { start, startTime } = form.getValues();
        const [HH, mm] = startTime.split(':');
        const startDate = dayjs(start).hour(+HH).minute(+mm).second(0).millisecond(0).toDate();

        Tasks.app.openPopup(CustomRepeatPopup, {
          onClose: () => {
            form.setValue('repeat', RepeatEnum.NONE);
          },
          params: {
            customRepeatValue: presenter.customRepeat?.key,
            startDate,
            onSubmit: (params: { key: string; title: string }) => {
              presenter.setCustomRepeat(params);
              form.setValue('repeat', params.key);
            },
          },
        });
      }

      if (
        props.isCreate &&
        ![RepeatEnum.CUSTOM, presenter.customRepeat?.key].includes(currentRepeatValue)
      ) {
        presenter.setCustomRepeat(null);
      }
    },

    getMinimizedTitleInfo(): string {
      return form.getValues().title || `(${t('calendar_notitle')})`;
    },

    getMinimizedTimeInfo(): string {
      const { start, end } = form.getValues();
      const formatStart = dayjs(start).format('ddd, MMM D HH:mm');
      const formatEnd = dayjs(end).format('HH:mm');

      return `${formatStart}-${formatEnd}`;
    },

    onChangeReminderItem(selectorValue: string, index: number): void {
      switch (selectorValue) {
        case ReminderTimeEnum.Custom:
          Tasks.app.openPopup(CustomReminderPopup, {
            onClose: () => {
              update(index, { value: ReminderTimeEnum.None });
            },
            params: {
              onSubmit: (params: { key: { time: string; unit: string } }) => {
                const customTime = mapReminderToMinutes(Number(params.key.time), params.key.unit);
                update(index, { value: customTime.toString() });
              },
            },
          });
          break;
        case ReminderTimeEnum.None:
          update(index, { value: ReminderTimeEnum.None });
          break;
        default:
          update(index, { value: selectorValue });
      }
    },

    onChangeDescription(value: string): void {
      props.updateEvent({ description: value });
    },
  }));

  const form = useForm<IFormData>({
    mode: 'onTouched',
    criteriaMode: 'all',
    defaultValues: presenter.initialValues,
    resolver: customResolver,
  });
  const titleValue = form.watch('title');
  const repeatValue = form.watch('repeat');
  const participantsValue = form.watch('participants');

  const { fields, append, replace, update } = useFieldArray({
    name: 'reminders',
    control: form.control,
  });
  const reminderValue = form.watch('reminders');
  const controlledReminderFields = fields.map((field, index) => {
    return {
      ...field,
      ...reminderValue[index],
    };
  });

  useEffect(() => {
    presenter.initialize();
  }, []);

  useEffect(() => {
    if (props.event) {
      form.setValue('start', props.event.start, {
        shouldValidate: true,
      });

      const end = props.event.allDay ? new Date(props.event.end.getTime() - 1) : props.event.end;

      form.setValue('end', end, {
        shouldValidate: true,
      });
      form.setValue('allDay', !!props.event.allDay, {
        shouldValidate: true,
      });

      const newStartTime = dayjs(props.event.start).format('HH:mm');
      if (form.getValues().startTime !== newStartTime) {
        form.setValue('startTime', newStartTime, {
          shouldValidate: true,
        });
      }

      const newEndTime = dayjs(end).format('HH:mm');
      if (form.getValues().endTime !== newEndTime) {
        form.setValue('endTime', newEndTime, {
          shouldValidate: true,
        });
      }

      if (props.event.title !== `(${t('calendar_notitle')})`) {
        form.setValue('title', props.event.title, { shouldValidate: true });
      }

      if (props.event.description) {
        form.setValue('description', props.event.description, { shouldValidate: true });
      }
    }
  }, [props.event]);

  useEffect(() => {
    if (props.isCreate && titleValue && titleValue !== props.event.title) {
      presenter.onChangeTitle(titleValue);
    }
  }, [props.isCreate, props.event, titleValue]);

  useEffect(() => {
    if (props.event.allDay) {
      presenter.setDiff(dayjs(props.event.end).diff(props.event.start, 'day'));
    } else {
      presenter.setDiffMinutes(dayjs(props.event.end).diff(props.event.start, 'minutes'));
    }
  }, []);

  useEffect(() => {
    if (props.participantsInfo) {
      presenter.setParticipants(props.participantsInfo);
    }
  }, [props.participantsInfo]);

  useEffect(() => {
    const formParticipants = form.getValues().participants;
    const addedParticipants: IParticipant[] = [];

    formParticipants.forEach((formParticipant) => {
      if (props.event.participants.find((participant) => participant.email === formParticipant))
        return;
      addedParticipants.push({
        email: formParticipant,
        confirmationType: ConfirmationType.NO_ANSWER,
      });
    });

    props.updateEvent({
      participants: [...props.event.participants, ...addedParticipants],
    });
  }, [participantsValue]);

  useEffect(() => {
    form.setFocus('title');
  }, [form.setFocus]);

  useEffect(() => {
    const isValue = presenter.repeatItems.find((item) => item.key === repeatValue);
    if (!isValue && repeatValue) {
      presenter.setCustomRepeat({
        key: repeatValue,
        title: getHumanizeRRule(repeatValue),
      });
    }

    props.updateEvent({ recurrenceType: repeatValue });
  }, [repeatValue]);

  useEffect(() => {
    reminderValue.forEach((reminder, index) => {
      if (reminder.value === ReminderTimeEnum.None && index !== reminderValue.length - 1) {
        replace(reminderValue.filter((reminder) => reminder.value !== ReminderTimeEnum.None));
        return;
      }
    });
    if (
      reminderValue.length === 0 ||
      (reminderValue.length < REMINDERS_MAX_LENGTH &&
        reminderValue[reminderValue.length - 1].value !== ReminderTimeEnum.None)
    ) {
      append({ value: ReminderTimeEnum.None });
    }

    const ramindersForUpdate = reminderValue.filter((item) => item.value !== ReminderTimeEnum.None);

    props.updateEvent({
      reminders: ramindersForUpdate.map((reminder) => ({
        type: ReminderType.EMAIL,
        time: Number(reminder.value),
        unit: 'minutes',
      })),
    });
  }, [reminderValue]);

  const removeChip = (id: string): void => {
    const removedUser = presenter.selectedParticipants.find((participant) => participant.id === id);

    form.setValue(
      'participants',
      participantsValue.filter((email) => email !== (removedUser?.email || id))
    );

    if (removedUser) {
      presenter.removeParticipant(removedUser.email);
    }
  };

  return { presenter, form, removeChip, participantsValue, controlledReminderFields };
}
